import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
    Area,
    AreaChart,
    CartesianGrid,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';
import { AuthContext } from '_store';
import { ThemeContext } from 'theme';
import { consumeNDJSONStream, queryLogFacets, queryLogHits, queryLogs, tailLogs } from './logsApi';
import { normalizeLogEntry, parseFacetOptions, parseHits, parseNDJSON, rangeBounds } from './logsState';
import './logs.css';

export { Logs };

const PAGE_SIZE = 200;
const MAX_VISIBLE_TAIL_ROWS = 1000;

const defaultRange = (authUser) => {
    const hours = Number(authUser?.default_time_range || 24);
    if (hours <= 1) return '1h';
    if (hours <= 6) return '6h';
    if (hours <= 24) return '24h';
    if (hours <= 168) return '7d';
    return '31d';
};

function Logs() {
    const { authUser, selectedOrganization } = useContext(AuthContext);
    const { resolvedTheme } = useContext(ThemeContext);
    const [queryInput, setQueryInput] = useState('*');
    const [submittedQuery, setSubmittedQuery] = useState('*');
    const [range, setRange] = useState(() => defaultRange(authUser));
    const [autoRefresh, setAutoRefresh] = useState('0');
    const [entries, setEntries] = useState([]);
    const [hits, setHits] = useState([]);
    const [facets, setFacets] = useState({ levels: [], services: [], hosts: [] });
    const [levelFilter, setLevelFilter] = useState('');
    const [serviceFilter, setServiceFilter] = useState('');
    const [hostFilter, setHostFilter] = useState('');
    const [expandedRow, setExpandedRow] = useState(null);
    const [offset, setOffset] = useState(0);
    const [hasMore, setHasMore] = useState(false);
    const [loading, setLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [tailing, setTailing] = useState(false);
    const [stoppingTail, setStoppingTail] = useState(false);
    const [error, setError] = useState('');
    const [refreshNonce, setRefreshNonce] = useState(0);
    const queryAbortRef = useRef(null);
    const tailAbortRef = useRef(null);
    const facetsCacheKeyRef = useRef('');

    const makePayload = useCallback((targetOffset = 0) => ({
        query: submittedQuery || '*',
        ...rangeBounds(range),
        limit: PAGE_SIZE,
        offset: targetOffset,
    }), [range, submittedQuery]);

    const loadSearch = useCallback(async () => {
        if (!selectedOrganization || tailing) return undefined;
        queryAbortRef.current?.abort();
        const controller = new AbortController();
        queryAbortRef.current = controller;
        setLoading(true);
        setError('');
        setExpandedRow(null);

        try {
            const payload = makePayload(0);
            const facetsCacheKey = `${selectedOrganization}:${submittedQuery}:${range}`;
            const shouldLoadFacets = facetsCacheKeyRef.current !== facetsCacheKey;
            const [logsText, hitsText, facetsText] = await Promise.all([
                queryLogs(payload, controller.signal),
                queryLogHits(payload, controller.signal),
                shouldLoadFacets ? queryLogFacets(payload, controller.signal) : Promise.resolve(null),
            ]);
            if (controller.signal.aborted) return undefined;
            const nextEntries = parseNDJSON(logsText)
                .map(normalizeLogEntry)
                .sort((a, b) => new Date(b.time) - new Date(a.time));
            setEntries(nextEntries);
            setHits(parseHits(hitsText));
            if (facetsText != null) {
                setFacets(parseFacetOptions(facetsText));
                facetsCacheKeyRef.current = facetsCacheKey;
            }
            setOffset(0);
            setHasMore(nextEntries.length === PAGE_SIZE);
        } catch (requestError) {
            if (requestError.name !== 'AbortError') {
                setError(requestError.message || 'Unable to query VictoriaLogs.');
                setEntries([]);
                setHits([]);
            }
        } finally {
            if (queryAbortRef.current === controller) {
                queryAbortRef.current = null;
                setLoading(false);
            }
        }
        return () => controller.abort();
    }, [makePayload, range, selectedOrganization, submittedQuery, tailing]);

    useEffect(() => {
        loadSearch();
        return () => queryAbortRef.current?.abort();
    }, [loadSearch, refreshNonce]);

    useEffect(() => {
        queryAbortRef.current?.abort();
        tailAbortRef.current?.abort();
        setTailing(false);
        setStoppingTail(false);
        setEntries([]);
        setHits([]);
        setFacets({ levels: [], services: [], hosts: [] });
        facetsCacheKeyRef.current = '';
        setLevelFilter('');
        setServiceFilter('');
        setHostFilter('');
        setOffset(0);
        setError('');
        setRefreshNonce((value) => value + 1);
    }, [selectedOrganization]);

    useEffect(() => {
        const seconds = Number(autoRefresh);
        if (!seconds || tailing) return undefined;
        const timer = window.setInterval(() => setRefreshNonce((value) => value + 1), seconds * 1000);
        return () => window.clearInterval(timer);
    }, [autoRefresh, tailing]);

    useEffect(() => () => {
        queryAbortRef.current?.abort();
        tailAbortRef.current?.abort();
    }, []);

    const handleRun = () => {
        tailAbortRef.current?.abort();
        setTailing(false);
        setStoppingTail(false);
        const nextQuery = queryInput.trim() || '*';
        setSubmittedQuery(nextQuery);
        setOffset(0);
        setRefreshNonce((value) => value + 1);
    };

    const handleLoadOlder = async () => {
        if (loadingMore || !hasMore) return;
        const nextOffset = offset + PAGE_SIZE;
        setLoadingMore(true);
        setError('');
        try {
            const text = await queryLogs(makePayload(nextOffset));
            const olderEntries = parseNDJSON(text)
                .map(normalizeLogEntry)
                .sort((a, b) => new Date(b.time) - new Date(a.time));
            setEntries((current) => [...current, ...olderEntries]);
            setOffset(nextOffset);
            setHasMore(olderEntries.length === PAGE_SIZE);
        } catch (requestError) {
            setError(requestError.message || 'Unable to load older logs.');
        } finally {
            setLoadingMore(false);
        }
    };

    const stopTail = useCallback(() => {
        if (!tailAbortRef.current) return;
        setStoppingTail(true);
        tailAbortRef.current.abort();
    }, []);

    const startTail = async () => {
        if (tailAbortRef.current) return;
        queryAbortRef.current?.abort();
        const controller = new AbortController();
        const nextQuery = queryInput.trim() || '*';
        tailAbortRef.current = controller;
        setSubmittedQuery(nextQuery);
        setEntries([]);
        setHits([]);
        setOffset(0);
        setHasMore(false);
        setExpandedRow(null);
        setLevelFilter('');
        setServiceFilter('');
        setHostFilter('');
        setError('');
        setTailing(true);
        setStoppingTail(false);
        try {
            const response = await tailLogs({ query: nextQuery }, controller.signal);
            await consumeNDJSONStream(response, (entry) => {
                const normalized = normalizeLogEntry(entry);
                setEntries((current) => [normalized, ...current].slice(0, MAX_VISIBLE_TAIL_ROWS));
            });
        } catch (requestError) {
            if (requestError.name !== 'AbortError') {
                setError(requestError.message || 'Live tail ended unexpectedly.');
            }
        } finally {
            if (tailAbortRef.current === controller) {
                tailAbortRef.current = null;
                setTailing(false);
                setStoppingTail(false);
            }
        }
    };

    const availableFacets = useMemo(() => {
        const levels = new Set(facets.levels);
        const services = new Set(facets.services);
        const hosts = new Set(facets.hosts);
        entries.forEach((entry) => {
            if (entry.level && entry.level !== 'unknown') levels.add(String(entry.level));
            if (entry.service && entry.service !== 'unknown') services.add(String(entry.service));
            if (entry.host && entry.host !== 'unknown') hosts.add(String(entry.host));
        });
        return {
            levels: [...levels].sort(),
            services: [...services].sort(),
            hosts: [...hosts].sort(),
        };
    }, [entries, facets]);

    const visibleEntries = useMemo(() => entries.filter((entry) => (
        (!levelFilter || String(entry.level) === levelFilter)
        && (!serviceFilter || String(entry.service) === serviceFilter)
        && (!hostFilter || String(entry.host) === hostFilter)
    )), [entries, levelFilter, serviceFilter, hostFilter]);

    const toggleLogRow = (rowKey, expanded) => {
        setExpandedRow(expanded ? null : rowKey);
    };

    const handleLogRowKeyDown = (event, rowKey, expanded) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        toggleLogRow(rowKey, expanded);
    };

    if (!selectedOrganization) {
        return <LogsEmptyState message="Select an organization to query its logs." />;
    }

    return (
        <div className="logs-page">
            <header className="logs-header">
                <div>
                    <div className="logs-eyebrow"><span></span>Observability</div>
                    <h1>Logs</h1>
                </div>
                <div className={`logs-status ${tailing ? 'live' : ''}`}>
                    <span></span>{tailing ? (stoppingTail ? 'Stopping live tail' : 'Live tail active') : 'Historical search'}
                </div>
            </header>

            <section className="logs-query-card">
                <label htmlFor="logs-query">LogsQL query</label>
                <div className="logs-query-row">
                    <input
                        id="logs-query"
                        value={queryInput}
                        onChange={(event) => setQueryInput(event.target.value)}
                        onKeyDown={(event) => {
                            if (event.key === 'Enter' && !event.shiftKey) handleRun();
                        }}
                        spellCheck="false"
                        placeholder="* or error service:=api"
                        disabled={tailing}
                    />
                    <button type="button" className="logs-primary-btn" onClick={handleRun} disabled={loading || tailing}>
                        <span className="material-symbols-outlined">search</span>Run
                    </button>
                    {loading ? (
                        <button type="button" className="logs-secondary-btn" onClick={() => queryAbortRef.current?.abort()}>
                            <span className="material-symbols-outlined">stop_circle</span>Cancel
                        </button>
                    ) : tailing ? (
                        <button type="button" className="logs-stop-btn" onClick={stopTail} disabled={stoppingTail}>
                            <span className="material-symbols-outlined">stop</span>Stop tail
                        </button>
                    ) : (
                        <button type="button" className="logs-secondary-btn" onClick={startTail}>
                            <span className="material-symbols-outlined">stream</span>Live tail
                        </button>
                    )}
                </div>

                <div className="logs-controls">
                    <ControlSelect label="Time range" value={range} onChange={(value) => { setRange(value); setOffset(0); }} disabled={tailing} options={[
                        ['15m', 'Last 15 minutes'], ['1h', 'Last hour'], ['6h', 'Last 6 hours'],
                        ['24h', 'Last 24 hours'], ['7d', 'Last 7 days'], ['31d', 'Last 31 days'],
                    ]} />
                    <ControlSelect label="Auto refresh" value={autoRefresh} onChange={setAutoRefresh} disabled={tailing} options={[
                        ['0', 'Off'], ['5', '5 seconds'], ['15', '15 seconds'], ['30', '30 seconds'],
                    ]} />
                    <ControlSelect label="Level" value={levelFilter} onChange={setLevelFilter} options={[
                        ['', 'All levels'], ...availableFacets.levels.map((value) => [value, value]),
                    ]} />
                    <ControlSelect label="Service" value={serviceFilter} onChange={setServiceFilter} options={[
                        ['', 'All services'], ...availableFacets.services.map((value) => [value, value]),
                    ]} />
                    <ControlSelect label="Host" value={hostFilter} onChange={setHostFilter} options={[
                        ['', 'All hosts'], ...availableFacets.hosts.map((value) => [value, value]),
                    ]} />
                </div>
            </section>

            {error && (
                <div className="logs-error" role="alert">
                    <span className="material-symbols-outlined">error</span>
                    <div><strong>VictoriaLogs request failed</strong><span>{error}</span></div>
                </div>
            )}

            <section className="logs-chart-card">
                <div className="logs-section-heading">
                    <div><span className="material-symbols-outlined">monitoring</span>Log volume</div>
                    <span>{hits.length ? `${hits.length} buckets` : 'No histogram data'}</span>
                </div>
                <div className="logs-chart">
                    {hits.length ? (
                        <ResponsiveContainer width="100%" height="100%">
                            <AreaChart data={hits} margin={{ top: 8, right: 12, bottom: 0, left: -16 }}>
                                <defs>
                                    <linearGradient id="logsHits" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="var(--color-accent-teal)" stopOpacity={0.32} />
                                        <stop offset="95%" stopColor="var(--color-accent-teal)" stopOpacity={0.02} />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-chart-grid)" vertical={false} />
                                <XAxis dataKey="time" tickFormatter={formatChartTime} minTickGap={48} tick={{ fontSize: 11, fill: 'var(--color-text-muted)' }} axisLine={false} tickLine={false} />
                                <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: 'var(--color-text-muted)' }} axisLine={false} tickLine={false} />
                                <Tooltip
                                    labelFormatter={(value) => formatTimestamp(value)}
                                    formatter={(value) => [value, 'Logs']}
                                    contentStyle={resolvedTheme === 'dark' ? { background: 'var(--color-surface-card)', border: '1px solid var(--color-divider)', color: 'var(--color-text-main)' } : undefined}
                                    itemStyle={resolvedTheme === 'dark' ? { color: 'var(--color-text-main)' } : undefined}
                                    labelStyle={resolvedTheme === 'dark' ? { color: 'var(--color-text-strong)' } : undefined}
                                />
                                <Area type="monotone" dataKey="hits" stroke="var(--color-accent-teal)" strokeWidth={2} fill="url(#logsHits)" />
                            </AreaChart>
                        </ResponsiveContainer>
                    ) : <div className="logs-chart-empty">Run a query to view log volume.</div>}
                </div>
            </section>

            <section className="logs-results-card">
                <div className="logs-section-heading">
                    <div><span className="material-symbols-outlined">subject</span>Log entries</div>
                    <span>{visibleEntries.length} shown{visibleEntries.length !== entries.length ? ` of ${entries.length}` : ''}</span>
                </div>
                {loading && entries.length === 0 ? (
                    <div className="logs-loading"><span></span>Querying VictoriaLogs…</div>
                ) : visibleEntries.length === 0 ? (
                    <LogsEmptyState message={tailing ? 'Waiting for live log entries.' : 'No logs matched this query and filter combination.'} compact />
                ) : (
                    <div className="logs-table-wrap">
                        <table className="logs-table">
                            <thead><tr><th>Time</th><th>Level</th><th>Host</th><th>Service</th><th>Message</th><th></th></tr></thead>
                            <tbody>
                                {visibleEntries.map((entry, index) => {
                                    const rowKey = `${entry.time}-${offset}-${index}`;
                                    const detailId = `${rowKey}-details`;
                                    const expanded = expandedRow === rowKey;
                                    return [
                                        <tr
                                            key={rowKey}
                                            className="logs-row"
                                            onClick={() => toggleLogRow(rowKey, expanded)}
                                            onKeyDown={(event) => handleLogRowKeyDown(event, rowKey, expanded)}
                                            role="button"
                                            tabIndex={0}
                                            aria-expanded={expanded}
                                            aria-controls={detailId}
                                        >
                                            <td className="logs-time" data-label="Time">{formatTimestamp(entry.time)}</td>
                                            <td data-label="Level"><LevelBadge value={entry.level} /></td>
                                            <td className="logs-dimension" data-label="Host">{entry.host}</td>
                                            <td className="logs-dimension" data-label="Service">{entry.service}</td>
                                            <td className="logs-message" data-label="Message">{entry.message}</td>
                                            <td className="logs-expand-cell">
                                                <span className="material-symbols-outlined logs-expand">{expanded ? 'expand_less' : 'expand_more'}</span>
                                            </td>
                                        </tr>,
                                        expanded && (
                                            <tr key={detailId} id={detailId} className="logs-details-row">
                                                <td colSpan="6"><pre>{JSON.stringify(entry.raw, null, 2)}</pre></td>
                                            </tr>
                                        ),
                                    ];
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
                {!tailing && hasMore && (
                    <div className="logs-load-more">
                        <button type="button" className="logs-secondary-btn" onClick={handleLoadOlder} disabled={loadingMore}>
                            <span className={`material-symbols-outlined ${loadingMore ? 'logs-spin' : ''}`}>history</span>
                            {loadingMore ? 'Loading…' : 'Load older logs'}
                        </button>
                    </div>
                )}
            </section>
        </div>
    );
}

function ControlSelect({ label, value, onChange, options, disabled = false }) {
    return (
        <label className="logs-control">
            <span>{label}</span>
            <select value={value} onChange={(event) => onChange(event.target.value)} disabled={disabled}>
                {options.map(([optionValue, optionLabel]) => <option key={optionValue || 'all'} value={optionValue}>{optionLabel}</option>)}
            </select>
        </label>
    );
}

function LevelBadge({ value }) {
    const normalized = String(value || 'unknown').toLowerCase();
    const severity = normalized.includes('error') || normalized.includes('fatal') || normalized === '3'
        ? 'error'
        : normalized.includes('warn') || normalized === '4' ? 'warn'
            : normalized.includes('debug') ? 'debug' : 'info';
    return <span className={`logs-level ${severity}`}>{value || 'unknown'}</span>;
}

function LogsEmptyState({ message, compact = false }) {
    return (
        <div className={`logs-empty ${compact ? 'compact' : ''}`}>
            <span className="material-symbols-outlined">manage_search</span>
            <strong>No log entries</strong>
            <p>{message}</p>
        </div>
    );
}

function formatTimestamp(value) {
    if (!value) return '—';
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString();
}

function formatChartTime(value) {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? value : date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}
